import {
    GetScopesWithQueueProjectsQuery,
    Maybe,
    Scopes
} from "generated/graphql";
import { convertMaybeToUndefined } from "graphql/helpers/queryHelpers";
import { DateTime } from "luxon";
import { GeneratorType } from "./generatorType";
import { convertToRegionType, RegionType } from "./regionType";
import { ADMIN_TYPE } from "./roleType";
import {
    convertToScopeViewType,
    getCapacityCostsScopeViewFromRegionAndGenerator,
    getConstraintStackScopeViewsFromRegionAndGenerator,
    isKnownScopeViewType,
    ScopeViewType
} from "./scopeViewType";
import { getAdvancedStudiesFlag } from "./teamFeatureFlagType";
// Type-only: userType imports this module's values, so a value import would
// create a runtime cycle.
import type { User } from "./userType";

export const PJM_ALL_QUEUE_SCOPE = "pjm_all_queue";
export const PJM_LOAD_SCOPE = "pjm_load_scope";
export const MISO_2022_REALISTIC_SCOPE_TYPE = "miso_2022_realistic";
export const MISO_PESSIMISTIC_SCOPE_TYPE = "miso_2023_generators_included";
export const MISO_2022_MTEP_SCOPE_TYPE = "miso_2022_mtep";
export const SPP_CPP_ICS_PESSIMISTIC_SCOPE_TYPE = "spp_cpp_ics_pessimistic";
export const SPP_LOAD_SCOPE_TYPE = "spp_load";
export const ERCOT_2027_SCOPE_TYPE = "ercot_2027_scope";
export const ERCOT_LOAD_2028_SCOPE_TYPE = "ercot_load_2028_scope";
export const ERCOT_LOAD_2028_765KV_SCOPE_TYPE = "ercot_load_2028_765kv_scope";
export const WECC_BASE_SCOPE_TYPE = "wecc_base";
export const WECC_LATE_STAGE_GENERATORS_SCOPE_TYPE =
    "wecc_late_stage_generators";
export const WECC_ALL_GENERATORS_SCOPE_TYPE = "wecc_all_generators";
export const WECC_FUTURE_TERM_BASE_SCOPE_TYPE = "wecc_future_term_base";
export const WECC_FUTURE_TERM_LATE_STAGE_GENERATORS_SCOPE_TYPE =
    "wecc_future_term_late_stage_generators";
export const WECC_FUTURE_TERM_ALL_GENERATORS_SCOPE_TYPE =
    "wecc_future_term_all_generators";
export const WECC_LOAD_SCOPE_TYPE = "wecc_load_scope";
export const NYISO_2029_SCOPE_TYPE = "nyiso_2029_scope";
export const TVA_2028_WITH_PRIORS_SCOPE_TYPE = "tva_2028_with_priors";
export const ISONE_BASE_SCOPE_TYPE = "isone_base";
export const SOCO_BASE_SCOPE_TYPE = "soco_scope";
export const SOCO_LOAD_SCOPE_TYPE = "soco_load_scope";
export const DUKE_DISIS_2025_PHASE_1_SCOPE_TYPE =
    "DUKE_DISIS_2025_PHASE_1_SCOPE";
export const DUKE_LOAD_2032_SCOPE_TYPE = "DUKE_LOAD_2032_SCOPE";
export const CAISO_PRE_SCREEN_CLUSTER_16_SCOPE_TYPE =
    "caiso_pre_screen_cluster_16";

// Regions where the local studies (bus page / re-study) feature is supported.
// Per-team access is additionally gated by the per-region
// local_studies:<REGION> flag (see getLocalStudiesFlag).
export const LOCAL_STUDIES_REGIONS: ReadonlyArray<RegionType> = [
    RegionType.MISO,
    RegionType.WECC
];

export const isLocalStudiesEnabledForRegion = (region: RegionType): boolean =>
    LOCAL_STUDIES_REGIONS.includes(region);

export type ScopeName = string;

// TODO: decide whether this should be every scope that is not custom.
export const ADVANCED_STUDIES_SCOPES: ReadonlyArray<ScopeName> = [
    ERCOT_LOAD_2028_765KV_SCOPE_TYPE,
    ERCOT_LOAD_2028_SCOPE_TYPE,
    WECC_LOAD_SCOPE_TYPE
];

export const canUseAdvancedStudies = (
    user: User,
    region: RegionType,
    scope: ScopeName
): boolean =>
    user.role === ADMIN_TYPE &&
    user.teamFeatureFlags.includes(getAdvancedStudiesFlag(region)) &&
    ADVANCED_STUDIES_SCOPES.includes(scope);

type HasuraUserScopeConfig = {
    readonly __typename?: "user_scope_configs";
    readonly base_scope: string;
    readonly status: string;
    readonly created_at: string;
};

export type HasuraScope = Readonly<
    { __typename?: "scopes" } & Pick<
        Scopes,
        | "id"
        | "display_name"
        | "region"
        | "scope"
        | "scope_views"
        | "data_freshness_markdown"
        | "data_freshness_date"
        | "data_freshness_model"
        | "subscription_id"
    > & {
            readonly user_scope_config?: HasuraUserScopeConfig | null;
        }
>;

export enum UserScopeStatus {
    RUN_REQUESTED = "RUN_REQUESTED",
    RUN_SUBMITTED = "RUN_SUBMITTED",
    RUNNING = "RUNNING",
    COMPLETED = "COMPLETED",
    FAILED = "FAILED"
}

export const isKnownUserScopeStatus = (
    status: string
): status is UserScopeStatus => {
    return Object.values(UserScopeStatus).includes(status as UserScopeStatus);
};

const convertToUserScopeStatus = (status: string): UserScopeStatus => {
    if (isKnownUserScopeStatus(status)) {
        return status;
    }
    throw new Error(`Unexpected user scope status: ${status}`);
};

export const USER_SCOPE_STATUS_TO_DISPLAY_STRING: Record<
    UserScopeStatus,
    string
> = {
    [UserScopeStatus.RUN_REQUESTED]: "Run Requested",
    [UserScopeStatus.RUN_SUBMITTED]: "Run Submitted",
    [UserScopeStatus.RUNNING]: "Running",
    [UserScopeStatus.COMPLETED]: "Completed",
    [UserScopeStatus.FAILED]: "Failed"
};

const IN_PROGRESS_USER_SCOPE_STATUSES: ReadonlySet<UserScopeStatus> = new Set([
    UserScopeStatus.RUN_REQUESTED,
    UserScopeStatus.RUN_SUBMITTED,
    UserScopeStatus.RUNNING
]);

export const isUserScopeInProgress = (status: UserScopeStatus): boolean =>
    IN_PROGRESS_USER_SCOPE_STATUSES.has(status);

export type UserScopeConfig = {
    readonly baseScope: string;
    readonly status: UserScopeStatus;
    readonly createdAt: Date;
};

export type Scope = {
    readonly id: string;
    readonly display_name: string;
    readonly region: RegionType;
    readonly scope: ScopeName;
    readonly scope_views: ScopeViewType[];
    readonly data_freshness_markdown: string;
    readonly data_freshness_model: string | undefined;
    readonly data_freshness_date: DateTime | undefined;
    readonly subscription_id: number | undefined;
    readonly userScopeConfig: UserScopeConfig | undefined;
};

export const hasLocalStudyInProgress = (
    scopes: ReadonlyArray<Scope>
): boolean =>
    scopes.some(
        (scope) =>
            scope.userScopeConfig !== undefined &&
            isUserScopeInProgress(scope.userScopeConfig.status)
    );

const convertHasuraDataFreshnessDate = (
    date: Maybe<string> | undefined
): DateTime | undefined => {
    if (!date) {
        return undefined;
    }
    const parsed = DateTime.fromISO(date);
    return parsed.isValid ? parsed : undefined;
};

const convertHasuraUserScopeConfig = (
    hasuraConfig: HasuraUserScopeConfig | null | undefined
): UserScopeConfig | undefined => {
    if (!hasuraConfig) {
        return undefined;
    }
    return {
        baseScope: hasuraConfig.base_scope,
        status: convertToUserScopeStatus(hasuraConfig.status),
        createdAt: new Date(hasuraConfig.created_at)
    };
};

export const convertHasuraScopesToNiraScopes = (
    hasuraScopes: HasuraScope[]
): Scope[] => {
    return hasuraScopes.map((hasuraScope) => ({
        id: hasuraScope.id,
        display_name: hasuraScope.display_name,
        region: convertToRegionType(hasuraScope.region),
        scope: hasuraScope.scope,
        scope_views: (hasuraScope.scope_views as string[])
            .filter(isKnownScopeViewType)
            .map(convertToScopeViewType),
        data_freshness_markdown: hasuraScope.data_freshness_markdown.replace(
            /\\n/g,
            "\n"
        ),
        data_freshness_model: convertMaybeToUndefined(
            hasuraScope.data_freshness_model
        ),
        data_freshness_date: convertHasuraDataFreshnessDate(
            hasuraScope.data_freshness_date
        ),
        subscription_id: convertMaybeToUndefined(hasuraScope.subscription_id),
        userScopeConfig: convertHasuraUserScopeConfig(
            hasuraScope.user_scope_config
        )
    }));
};

/**
 * Whether a scope is a user-created local study (re-study), as opposed to
 * something a Nira pipeline produced.
 *
 * The three kinds of scope are already fully determined by two columns we
 * store anyway, so nothing needs a `type` column to tell them apart:
 *
 *     public / main pipeline   subscription_id null, no user_scope_config
 *     custom (bespoke, team)   subscription_id set,  no user_scope_config
 *     local study              subscription_id set,  user_scope_config set
 *
 * This predicate splits off the third; `isLocalStudyBaseEligible` below
 * isolates the first.
 */
export const isLocalStudy = (scope: Scope): boolean =>
    scope.userScopeConfig !== undefined;

/**
 * Whether a scope can seed a local study (re-study) as its base scope.
 *
 * Local studies only ever run on top of a main Nira regional pipeline, and
 * those pipelines produce the public scopes -- the ones with no
 * `subscription_id`. A scope that has a subscription is either a bespoke
 * custom scope built for a single team or another team member's local study,
 * and neither is a valid base: the re-study job is wired to the region's main
 * pipeline model (`miso_user_defined_scope_job` / `wecc_user_defined_scope_job`)
 * and never reads the base scope's model, so basing a study on one would
 * silently study the wrong assumptions. Hasura requires a subscription to
 * insert a scope, so every local study has a `subscription_id` and this check
 * subsumes the older "has no user_scope_config" test.
 *
 * Deliberately narrower than "is a Nira scope": custom scopes still belong
 * under "Nira Scopes" in the bus page scope picker (they are Nira-built results
 * a team should be able to view), they just cannot seed a re-study. Nothing
 * enforces this server-side -- `user_scope_configs.base_scope` is free text --
 * so this filter is the only guardrail.
 */
export const isLocalStudyBaseEligible = (scope: Scope): boolean =>
    scope.subscription_id === undefined;

export const getScopeFromScopes = (
    scopes: ReadonlyArray<Scope>,
    scopeValue: ScopeName
): Scope | undefined => {
    return scopes.find((s) => s.scope === scopeValue);
};

export const getBaseScopesForFuelTypes = (
    eligibleBaseScopes: ReadonlyArray<Scope>,
    scopeToFuelTypesForBus: ReadonlyMap<
        ScopeName,
        ReadonlyArray<GeneratorType>
    >,
    scopesWithProjects: GetScopesWithQueueProjectsQuery | undefined,
    fuelTypes: ReadonlyArray<GeneratorType>,
    region: RegionType
): ReadonlyArray<Scope> => {
    const scopeQueueRows = scopesWithProjects?.queue_projects ?? [];
    return eligibleBaseScopes.filter((s) => {
        const supported = scopeToFuelTypesForBus.get(s.scope);
        if (
            fuelTypes.length > 0 &&
            (supported === undefined ||
                !fuelTypes.every((ft) => supported.includes(ft)))
        ) {
            return false;
        }
        if (fuelTypes.length === 0) {
            return scopeQueueRows.some((qp) => qp.scope === s.scope);
        }
        const requiredScopeViews = fuelTypes.map((ft) =>
            getCapacityCostsScopeViewFromRegionAndGenerator(region, ft)
        );
        return requiredScopeViews.every((sv) =>
            scopeQueueRows.some(
                (qp) => qp.scope === s.scope && qp.scope_view === sv
            )
        );
    });
};

// NOTE: Known bug for ERCOT — scope_views in the Scopes table is populated
// from capacity costs, so it won't have all the constraint stack scope views.
// This means ERCOT may return no acceptable scopes/scope names.

/**
 * Lower-level helper: given a map of scope name → scope views, returns which
 * scope names have all the required scope views for the given region and
 * generator type.
 */
export const getAcceptableScopeNamesForRegionAndGenerator = (
    scopeViewsByScope: ReadonlyMap<ScopeName, ReadonlyArray<ScopeViewType>>,
    region: RegionType,
    generatorType: GeneratorType
): ReadonlyArray<ScopeName> => {
    const requiredScopeViews =
        getConstraintStackScopeViewsFromRegionAndGenerator(
            region,
            generatorType
        );
    const result: ScopeName[] = [];
    scopeViewsByScope.forEach((scopeViews, scopeName) => {
        if (
            requiredScopeViews.every((required) =>
                scopeViews.includes(required)
            )
        ) {
            result.push(scopeName);
        }
    });
    return result;
};

export const getAcceptableScopesForRegionAndGenerator = (
    scopes: ReadonlyArray<Scope>,
    region: RegionType,
    generatorType: GeneratorType
): ReadonlyArray<Scope> => {
    const scopeViewsByScope = new Map<ScopeName, ReadonlyArray<ScopeViewType>>(
        scopes
            .filter((s) => s.region === region)
            .map((s) => [s.scope, s.scope_views])
    );
    const validNames = getAcceptableScopeNamesForRegionAndGenerator(
        scopeViewsByScope,
        region,
        generatorType
    );
    return scopes.filter((s) => validNames.includes(s.scope));
};

export const hasMultipleScopesForRegionAndGeneratorType = (
    scopes: ReadonlyArray<Scope>,
    region: RegionType,
    generatorType: GeneratorType
): boolean => {
    const availableScopes = getAcceptableScopesForRegionAndGenerator(
        scopes,
        region,
        generatorType
    );
    return availableScopes.length > 1;
};
